'use strict'

// HELPERS

// Cache for memoizing getItem transformations to reduce TBT
// Uses WeakMap so products can be garbage collected when no longer referenced
const itemCache = new WeakMap()

import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getSiteEnvVarValue} from '../utils/site-config'

const INTERACTION_LOCATION = {
    MENU_NAVIGATION: 'menu_navigation',
    DIRECT: 'direct',
    PDP: 'pdp',
    PLP: 'plp',
    HOME_PAGE: 'hp',
    SEARCH: 'search_process',
    WISHLIST: 'wishlist_page',
    MINI_CART: 'mini_cart',
    CART: 'cart_page'
}

/** Predefined sign_up interaction_location values (TSD). */
const SIGN_UP_INTERACTION_LOCATION = {
    NATIVE: 'native',
    CHECKOUT: 'checkout',
    GUEST_TO_MEMBER: 'guest_to_member',
    LOYALTY_ENROLL: 'loyalty_enroll'
}

const SIGN_UP_METHODS = new Set(['google', 'apple', 'email_password', 'facebook'])

const GTM_MENU_NAVIGATION_KEY = 'gtmMenuNavigation'
const GTM_LAST_PATHNAME_KEY = 'gtmLastPathname'
const GTM_REFERRER_PATHNAME_KEY = 'gtmReferrerPathname'
const GTM_SELECT_ITEM_LOCATION_KEY = 'gtmSelectItemLocation'
const GTM_SIGN_UP_INTERACTION_LOCATION_KEY = 'gtmSignUpInteractionLocation'
const GTM_PURCHASE_SENT_KEY = 'gtmPurchaseSentOrders'
// We only remember the last 20 orders. That is far more than anyone would reopen an old
// confirmation page for, and it keeps the list from growing with every order placed.
const GTM_PURCHASE_SENT_LIMIT = 20
const GTM_NAV_MENU_ATTR = 'data-gtm-nav'
const GTM_NAV_MENU_VALUE = 'menu'

const PDP_PATHNAME_REGEX = /(?:^|\/)p\/[^/]+\/[^/]+\.html(?:$|\?)/i
const LEGACY_PRODUCT_PATHNAME_REGEX = /(?:^|\/)product\/[^/]+/i
const PLP_PATHNAME_REGEX = /(?:^|\/)(?:category\/|(?:[a-z]{2}(?:-[a-z]{2})?\/)?[gd]\/|search(?:$|\?))/i
const HOME_PATHNAME_REGEX = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/?)?$/i
const SEARCH_PATHNAME_REGEX = /(?:^|\/)search(?:$|\?|\/)/i
const WISHLIST_PATHNAME_REGEX = /(?:^|\/)(?:account\/)?wishlist/i
const CART_PATHNAME_REGEX = /(?:^|\/)cart(?:$|\?|\/)/i
const CHECKOUT_CONFIRMATION_PATHNAME_REGEX = /(?:^|\/)checkout\/confirmation(?:$|\?|\/)/i
const CHECKOUT_PATHNAME_REGEX = /(?:^|\/)checkout(?:$|\?|\/)/i
const LOYALTY_PATHNAME_REGEX = /(?:^|\/)(?:my-rewards|rewards|loyalty)(?:$|\?|\/)/i

/**
 * Normalizes a pathname for comparison (trim trailing slash except root).
 * @param {string} pathname - Pathname to normalize.
 * @returns {string} Normalized pathname.
 */
const normalizePathname = (pathname = '') => {
    if (!pathname || pathname === '/') return '/'
    return pathname.replace(/\/+$/, '')
}

/**
 * Resolves sign_up interaction_location from the URL where registration started
 * (auth drawer page, /registration, confirmation, etc.).
 * @param {string} [pathname] - Pathname when the sign-up flow began.
 * @returns {string} One of SIGN_UP_INTERACTION_LOCATION values.
 */
const resolveSignUpInteractionLocation = (pathname = '') => {
    const path = normalizePathname(pathname)
    if (CHECKOUT_CONFIRMATION_PATHNAME_REGEX.test(path)) {
        return SIGN_UP_INTERACTION_LOCATION.GUEST_TO_MEMBER
    }
    if (CHECKOUT_PATHNAME_REGEX.test(path)) {
        return SIGN_UP_INTERACTION_LOCATION.CHECKOUT
    }
    if (LOYALTY_PATHNAME_REGEX.test(path)) {
        return SIGN_UP_INTERACTION_LOCATION.LOYALTY_ENROLL
    }
    return SIGN_UP_INTERACTION_LOCATION.NATIVE
}

/**
 * Returns whether the pathname represents a PDP route.
 * @param {string} pathname - Pathname to classify.
 * @returns {boolean} True when the path is a product detail page.
 */
const isPdpPathname = (pathname) =>
    PDP_PATHNAME_REGEX.test(pathname) || LEGACY_PRODUCT_PATHNAME_REGEX.test(pathname)

/**
 * Returns whether the pathname represents a PLP/search browse route.
 * @param {string} pathname - Pathname to classify.
 * @returns {boolean} True when the path is a product list or search page.
 */
const isPlpPathname = (pathname) => PLP_PATHNAME_REGEX.test(pathname)

/**
 * Returns whether the pathname represents the homepage.
 * @param {string} pathname - Pathname to classify.
 * @returns {boolean} True when the path is the localized or root homepage.
 */
const isHomePathname = (pathname) => HOME_PATHNAME_REGEX.test(pathname)

/**
 * Returns whether the pathname represents a search results route.
 * @param {string} pathname - Pathname to classify.
 * @returns {boolean} True when the path is a search page.
 */
const isSearchPathname = (pathname) => SEARCH_PATHNAME_REGEX.test(pathname)

/**
 * Returns whether the pathname represents a wishlist route.
 * @param {string} pathname - Pathname to classify.
 * @returns {boolean} True when the path is a wishlist page.
 */
const isWishlistPathname = (pathname) => WISHLIST_PATHNAME_REGEX.test(pathname)

/**
 * Returns whether the pathname represents a cart route.
 * @param {string} pathname - Pathname to classify.
 * @returns {boolean} True when the path is the cart page.
 */
const isCartPathname = (pathname) => CART_PATHNAME_REGEX.test(pathname)

/**
 * Safely reads a sessionStorage value.
 * @param {string} key - Storage key.
 * @returns {string|null} Stored value or null when unavailable.
 */
const getSessionStorageItem = (key) => {
    try {
        if (typeof window === 'undefined') return null
        return window.sessionStorage.getItem(key)
    } catch (error) {
        return null
    }
}

/**
 * Safely writes a sessionStorage value.
 * @param {string} key - Storage key.
 * @param {string} value - Value to store.
 * @returns {void}
 */
const setSessionStorageItem = (key, value) => {
    if (typeof window === 'undefined') return
    window.sessionStorage.setItem(key, value)
}

/**
 * Safely removes a sessionStorage value.
 * @param {string} key - Storage key.
 * @returns {void}
 */
const removeSessionStorageItem = (key) => {
    if (typeof window === 'undefined') return
    window.sessionStorage.removeItem(key)
}

/**
 * @param {string} value - Candidate interaction_location.
 * @returns {boolean} True when value is a predefined sign_up location.
 */
const isValidSignUpInteractionLocation = (value) =>
    Object.values(SIGN_UP_INTERACTION_LOCATION).includes(value)

/**
 * Persists sign_up interaction_location for later fire (e.g. after /account redirect).
 * @param {string} interactionLocation - Predefined sign_up location value.
 * @returns {void}
 */
const persistSignUpInteractionLocation = (interactionLocation) => {
    if (!isValidSignUpInteractionLocation(interactionLocation)) return
    setSessionStorageItem(GTM_SIGN_UP_INTERACTION_LOCATION_KEY, interactionLocation)
}

/**
 * Reads and clears the persisted sign_up interaction_location.
 * @returns {string} Stored location or native fallback.
 */
const consumeSignUpInteractionLocation = () => {
    const stored = getSessionStorageItem(GTM_SIGN_UP_INTERACTION_LOCATION_KEY)
    removeSessionStorageItem(GTM_SIGN_UP_INTERACTION_LOCATION_KEY)
    return isValidSignUpInteractionLocation(stored)
        ? stored
        : SIGN_UP_INTERACTION_LOCATION.NATIVE
}

/**
 * Normalizes sign_up/login method to TSD-allowed lowercase values.
 * @param {string} method - Raw method (e.g. Google, email_password).
 * @returns {string|null} Allowed method or null when invalid/empty.
 */
const normalizeSignUpMethod = (method) => {
    if (!method || typeof method !== 'string') return null
    const normalized = method.toLowerCase().trim()
    if (SIGN_UP_METHODS.has(normalized)) return normalized
    if (normalized === 'email' || normalized === 'password') return 'email_password'
    return null
}

/**
 * Reads the orders we have already sent a purchase event for in this browser.
 * @returns {string[]} Order numbers, oldest first.
 */
const getTrackedPurchaseOrders = () => {
    try {
        if (typeof window === 'undefined' || !window.localStorage) return []
        const stored = JSON.parse(window.localStorage.getItem(GTM_PURCHASE_SENT_KEY))
        return Array.isArray(stored) ? stored : []
    } catch (error) {
        return []
    }
}

/**
 * Remembers an order so we never send a second purchase event for it.
 * The oldest entries fall off once the list is full.
 * @param {string} orderNo - Order number to remember.
 * @returns {void}
 */
const trackPurchaseOrder = (orderNo) => {
    try {
        if (typeof window === 'undefined' || !window.localStorage) return
        const orders = getTrackedPurchaseOrders()
            .filter((trackedOrderNo) => trackedOrderNo !== orderNo)
            .concat(orderNo)
            .slice(-GTM_PURCHASE_SENT_LIMIT)
        window.localStorage.setItem(GTM_PURCHASE_SENT_KEY, JSON.stringify(orders))
    } catch (error) {
        // Storage can be full or blocked in private browsing, nothing we can do about it here
    }
}

/**
 * Consumes a previously marked menu-navigation destination if it matches the current path.
 * @param {string} currentPathname - Current location pathname.
 * @returns {boolean} True when the current path was reached via menu navigation.
 */
const consumeMenuNavigationSource = (currentPathname) => {
    const markedPathname = getSessionStorageItem(GTM_MENU_NAVIGATION_KEY)
    removeSessionStorageItem(GTM_MENU_NAVIGATION_KEY)

    if (!markedPathname) return false

    return normalizePathname(markedPathname) === normalizePathname(currentPathname)
}

/**
 * Tracks SPA pathname history used to resolve view_item_list interaction_location.
 * @param {string} pathname - Current location pathname.
 * @returns {void}
 */
const trackGtmPathname = (pathname) => {
    if (!pathname) return

    const lastPathname = getSessionStorageItem(GTM_LAST_PATHNAME_KEY)
    if (lastPathname && normalizePathname(lastPathname) !== normalizePathname(pathname)) {
        setSessionStorageItem(GTM_REFERRER_PATHNAME_KEY, lastPathname)
    }
    setSessionStorageItem(GTM_LAST_PATHNAME_KEY, pathname)
}

/**
 * Returns a same-origin document.referrer pathname when available.
 * @returns {string|null} Referrer pathname or null.
 */
const getSameOriginReferrerPathname = () => {
    try {
        if (typeof window === 'undefined' || !document.referrer) return null
        const referrerUrl = new URL(document.referrer)
        if (referrerUrl.origin !== window.location.origin) return null
        return referrerUrl.pathname
    } catch (error) {
        return null
    }
}

/**
 * Classifies a prior pathname into pdp, plp, hp, lp, or null when none apply.
 * PDP is evaluated before landing page because product urls also end in `.html`.
 * @param {string|null} pathname - Prior pathname.
 * @returns {string|null} INTERACTION_LOCATION.PDP, PLP, HOME_PAGE, or null.
 */
const classifyPathInteractionLocation = (pathname) => {
    if (!pathname) return null
    if (isPdpPathname(pathname)) return INTERACTION_LOCATION.PDP
    if (isPlpPathname(pathname)) return INTERACTION_LOCATION.PLP
    if (isHomePathname(pathname)) return INTERACTION_LOCATION.HOME_PAGE
    return null
}

/**
 * Resolves interaction_location for view_item_list based on menu source and prior route.
 * @param {string} currentPathname - Current PLP pathname.
 * @returns {string} One of INTERACTION_LOCATION values.
 */
const resolveViewItemListInteractionLocation = (currentPathname) => {
    if (consumeMenuNavigationSource(currentPathname)) {
        return INTERACTION_LOCATION.MENU_NAVIGATION
    }

    const lastPathname = getSessionStorageItem(GTM_LAST_PATHNAME_KEY)
    const storedReferrerPathname = getSessionStorageItem(GTM_REFERRER_PATHNAME_KEY)
    const cameFromPathname =
        lastPathname && normalizePathname(lastPathname) !== normalizePathname(currentPathname)
            ? lastPathname
            : storedReferrerPathname &&
              normalizePathname(storedReferrerPathname) !== normalizePathname(currentPathname)
            ? storedReferrerPathname
            : getSameOriginReferrerPathname()

    const classifiedLocation = classifyPathInteractionLocation(cameFromPathname)
    if (classifiedLocation) return classifiedLocation

    return INTERACTION_LOCATION.DIRECT
}

/**
 * Returns the previous same-session pathname used for GTM interaction_location.
 * @param {string} currentPathname - Current location pathname.
 * @returns {string|null} Prior pathname or null.
 */
const getCameFromPathname = (currentPathname) => {
    const lastPathname = getSessionStorageItem(GTM_LAST_PATHNAME_KEY)
    const storedReferrerPathname = getSessionStorageItem(GTM_REFERRER_PATHNAME_KEY)

    if (lastPathname && normalizePathname(lastPathname) !== normalizePathname(currentPathname)) {
        return lastPathname
    }
    if (storedReferrerPathname && normalizePathname(storedReferrerPathname) !== normalizePathname(currentPathname)) {
        return storedReferrerPathname
    }

    return getSameOriginReferrerPathname()
}

/**
 * Maps a prior route to a view_item interaction_location.
 * Search is checked before PLP because search URLs also match the PLP pattern.
 * Values outside the view_item set (hp, lp, menu) fall back to pdp.
 * @param {string|null} pathname - Prior pathname.
 * @returns {string} One of INTERACTION_LOCATION.SEARCH, WISHLIST, CART, PLP, or PDP.
 */
const classifyViewItemEntryLocation = (pathname) => {
    if (!pathname) return INTERACTION_LOCATION.PDP
    if (isSearchPathname(pathname)) return INTERACTION_LOCATION.SEARCH
    if (isWishlistPathname(pathname)) return INTERACTION_LOCATION.WISHLIST
    if (isCartPathname(pathname)) return INTERACTION_LOCATION.CART
    if (isPdpPathname(pathname)) return INTERACTION_LOCATION.PDP
    if (isPlpPathname(pathname)) return INTERACTION_LOCATION.PLP
    return INTERACTION_LOCATION.PDP
}

/**
 * Resolves interaction_location for the first view_item on a PDP.
 * then falls back to the previous route.
 * @param {string} currentPathname - Current PDP pathname.
 * @returns {string} Entry-source interaction_location.
 */
const resolveViewItemInteractionLocation = (currentPathname) => {
    const fromSelectItem = getSessionStorageItem(GTM_SELECT_ITEM_LOCATION_KEY)
    removeSessionStorageItem(GTM_SELECT_ITEM_LOCATION_KEY)

    if (
        fromSelectItem === INTERACTION_LOCATION.PLP ||
        fromSelectItem === INTERACTION_LOCATION.SEARCH ||
        fromSelectItem === INTERACTION_LOCATION.WISHLIST ||
        fromSelectItem === INTERACTION_LOCATION.CART ||
        fromSelectItem === INTERACTION_LOCATION.PDP ||
        fromSelectItem === INTERACTION_LOCATION.MINI_CART
    ) {
        return fromSelectItem
    }

    return classifyViewItemEntryLocation(getCameFromPathname(currentPathname))
}

/**
 * Resolves interaction_location for view_item fired from a product-tile color change.
 * @param {string} pathname - Current page pathname.
 * @param {boolean} isWishlistPage - True when the tile is rendered on a wishlist page.
 * @returns {string} One of INTERACTION_LOCATION.WISHLIST, SEARCH, or PLP.
 */
const resolveProductTileViewItemLocation = (pathname, isWishlistPage) => {
    if (isWishlistPage) return INTERACTION_LOCATION.WISHLIST
    if (isSearchPathname(pathname)) return INTERACTION_LOCATION.SEARCH
    return INTERACTION_LOCATION.PLP
}

const SEARCH_RESULT_PLP_NAME = 'search result'

/**
 * Formats a PLP add_to_cart interaction_location as `plp - {name}`.
 * @param {string} plpName - Category or list name.
 * @returns {string} Formatted PLP interaction_location.
 */
const formatAddToCartPlpLocation = (plpName) => {
    const name = typeof plpName === 'string' ? plpName.trim().toLowerCase().replace(/ /g, '_') : ''
    return name ? `${INTERACTION_LOCATION.PLP} - ${name}` : INTERACTION_LOCATION.PLP
}

/**
 * Resolves interaction_location for add_to_cart from the page where the add happened.
 * @param {string} pathname - Current location pathname.
 * @param {Object} [options] - Resolution options.
 * @param {boolean} [options.isWishlistPage] - True when adding from a wishlist surface.
 * @param {string} [options.plpName] - Name of the list the product was displayed in.
 * @param {string} [options.interactionLocation] - Location declared by the surface the add
 * happened on (e.g. the search modal), used instead of deriving one from the pathname.
 * @returns {string|null} One of pdp, wishlist_page, cart_page, `plp - {name}`, the declared
 * location, or null when the surface is unknown.
 */
const resolveAddToCartInteractionLocation = (pathname = '', options = {}) => {
    const isWishlistPage = Boolean(options.isWishlistPage)
    const plpName = options.plpName

    if (options.interactionLocation) {
        return options.interactionLocation
    }
    if (isWishlistPage || isWishlistPathname(pathname)) {
        return INTERACTION_LOCATION.WISHLIST
    }
    if (isPdpPathname(pathname)) {
        return INTERACTION_LOCATION.PDP
    }
    if (isCartPathname(pathname)) {
        return INTERACTION_LOCATION.CART
    }
    if (isSearchPathname(pathname)) {
        return formatAddToCartPlpLocation(plpName || SEARCH_RESULT_PLP_NAME)
    }
    if (plpName) {
        return formatAddToCartPlpLocation(plpName)
    }

    return null
}

/**
 * Capture-phase click handler that marks menu-navigation sources from nav containers.
 * @param {MouseEvent} event - Click event.
 * @returns {void}
 */
const handleGtmMenuNavigationClick = (event) => {
    const target = event?.target
    if (!target || typeof target.closest !== 'function') return

    const anchor = target.closest('a[href]')
    if (!anchor) return

    const menuContainer = anchor.closest(`[${GTM_NAV_MENU_ATTR}="${GTM_NAV_MENU_VALUE}"]`)
    if (!menuContainer) return

    const href = anchor.getAttribute('href')

    // Skip if href is not valid or is a hash
    if (!href || href === '#') return

    const url = new URL(href, window.location.origin)
    // Store the normalized pathname of the clicked link as the menu navigation source
    setSessionStorageItem(GTM_MENU_NAVIGATION_KEY, normalizePathname(url.pathname))
}

/**
 * Registers listeners that track pathname history and menu-navigation clicks for GTM.
 * @returns {Function} Cleanup function that removes the click listener.
 */
const initViewItemListInteractionTracking = () => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return () => {}
    }

    document.addEventListener('click', handleGtmMenuNavigationClick, true)

    return () => {
        document.removeEventListener('click', handleGtmMenuNavigationClick, true)
    }
}

let lastUserSessionPageKey = null
let queuedGtmEvents = []

const getCurrentPathname = () => (typeof window === 'undefined' ? '' : window.location.pathname)

/**
 * Page key for user_session ordering. Search stays on /search and only changes
 * `q` / `smartSearch`, so those must be part of the key or a new results view
 * will not send user_session (and later events will not wait for it).
 *
 * @param {Object} [location] - Optional location; defaults to window.location.
 * @returns {string}
 */
const getUserSessionPageKey = (location) => {
    const pathname = location?.pathname ?? getCurrentPathname()
    const search = location?.search ?? (typeof window === 'undefined' ? '' : window.location.search)
    const params = new URLSearchParams(search)
    const searchQuery = params.get('q') || ''
    const smartSearch = params.get('smartSearch') || ''
    return `${pathname}?q=${searchQuery}&smartSearch=${smartSearch}`
}

/**
 * Switching brands lands on session-redirect first, then navigates to the real page
 * skip user_session on the hop so it only fires once on the destination.
 *
 * @param {string} [pathname]
 * @returns {boolean}
 */
const isGtmHandoffPath = (pathname = '') =>
    /(?:^|\/)(session-redirect|sso-callback)\/?$/i.test(pathname)


/**
 * Resolves GTM country_code and language_code from the active multi-site locale.
 * Prefers locale.countryCode (e.g. en-CA → ca); falls back to the region segment of locale.id.
 *
 * @param {Object} locale - Locale object from useMultiSite(), with id and optional countryCode.
 * @returns {{country_code: string, language_code: string}}
 */
const getGtmLocaleCodes = (locale) => {
    const localeId = (locale?.id || '').replace('_', '-')
    const language = localeId.split('-')[0]?.toLowerCase()
    const country = (locale?.countryCode || localeId.split('-')[1] || 'ca').toLowerCase()
    return {
        country_code: country || 'ca',
        language_code: language || 'en'
    }
}

/**
 * Asynchronously generates a user object with SHA-256 hashed identifiers for GTM tracking.
 * Optimized to hash all values in parallel instead of sequentially to reduce TBT.
 *
 * @param {Object} customer - The customer object containing user information.
 * @returns {Promise<Object>} An object containing SHA-256 hashed user identifiers and membership tier.
 */
const getUser = async (customer) => {
    const isGuest = !customer.isRegistered

    // Hash all values in parallel instead of sequentially to reduce blocking time
    const [user_id_sha, customer_email_sha, customer_phone_sha, loyalty_id_sha] = await Promise.all([
        toSHA256(customer.customerId),
        !isGuest ? toSHA256(customer.email) : Promise.resolve(''),
        !isGuest ? toSHA256(customer.phoneHome) : Promise.resolve(''),
        !isGuest ? toSHA256(customer.c_loyalty_id) : Promise.resolve('')
    ])

    return {
        user_id_sha,
        customer_email_sha,
        customer_phone_sha,
        loyalty_id_sha,
        membership_tier: !isGuest ? customer.c_loyaltyMostRecentTier : ''
    }
}

/**
 * Determines and returns structured page information for Google Tag Manager (GTM) based on the current location object.
 *
 * @param {Object} location - The location object, typically from React Router or window.location, containing at least a pathname property.
 * @returns {Object} An object containing GTM page info properties such as page_name, page_category, and optionally page_section,
 *                   based on the matched route. Defaults to 'home' if no match is found or location is undefined.
 */
const getPageInfo = (location) => {
    if (!location) {
        return {
            page_name: 'home',
            page_category: 'home'
        }
    }

    switch (true) {
        case /\/confirmation/.test(location.pathname):
            return {
                page_name: 'checkout-success',
                page_category: 'checkout',
                page_section: 'success'
            }

        case /\/checkout/.test(location.pathname):
            return {
                page_name: 'checkout-review',
                page_category: 'checkout',
                page_section: 'review'
            }

        case /\/(category|([a-zA-Z]{2}\/)?[gd]\/)/.test(location.pathname):
            return {
                page_name: 'plp',
                page_category: 'browse'
            }

        case /\/search/.test(location.pathname):
            return {
                page_name: 'search',
                page_category: 'browse',
                page_section: 'search'
            }

        case /\/(product|([a-zA-Z]{2}\/)?p\/)/.test(location.pathname):
            return {
                page_name: 'pdp',
                page_category: 'product'
            }

        case /\/cart/.test(location.pathname):
            return {
                page_name: 'cart',
                page_category: 'checkout'
            }

        case /\/wishlist/.test(location.pathname):
            return {
                page_name: 'wishlist',
                page_category: 'account'
            }

        case /\/my-rewards/.test(location.pathname):
            return {
                page_name: 'my-rewards',
                page_category: 'account'
            }

        case /\/(account\/)?orders/.test(location.pathname):
            return {
                page_name: 'account-orders',
                page_category: 'account'
            }

        case /\/account/.test(location.pathname):
            return {
                page_name: 'account-home',
                page_category: 'account'
            }

        default:
            return {
                page_name: 'home',
                page_category: 'home'
            }
    }
}

const toFiniteNumber = (value) => {
    if (value === null || value === undefined) {
        return null
    }

    if (typeof value === 'string' && value.trim() === '') {
        return null
    }

    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
}

const getEventItemValue = (product) => {
    // Prefer the discounted/current price over the original price so items on
    // sale report the price the customer actually sees, not the pre-discount price.
    const directValue = toFiniteNumber(
        product?.priceAfterItemDiscount ?? product?.adjustedPrice ?? product?.price ?? product?.basePrice
    )

    if (directValue !== null) {
        return directValue
    }

    const totalValue = toFiniteNumber(product?.priceTotal ?? product?.adjustedPriceTotal)
    if (totalValue !== null) {
        const quantity = toFiniteNumber(product?.quantity)
        return quantity && quantity > 0 ? totalValue / quantity : totalValue
    }

    return 0
}

const getBasketValue = (basket) => {
    const productTotal = toFiniteNumber(basket?.productTotal)
    if (productTotal !== null) {
        return productTotal 
    }

    if (Array.isArray(basket?.productItems)) {
        return basket.productItems.reduce((sum, item) => {
            const price = toFiniteNumber(item?.price)
            const quantity = toFiniteNumber(item?.quantity) ?? 1
            return sum + (price ?? 0) * quantity
        }, 0)
    }

    return 0
}

const normalizePaymentType = (method) => {
    if (!method) return method

    const normalized = String(method).trim().toLowerCase().replace(/[_-]+/g, ' ')
    if (
        normalized === 'scheme' ||
        normalized === 'credit card' ||
        normalized === 'creditcard' ||
        normalized === 'credit cards' ||
        normalized === 'credit card payment' ||
        normalized === 'credit_card'
    ) {
        return 'credit card'
    }

    if (normalized === 'smartclixx') {
        return 'gift card'
    }

    return normalized
}

const getUnitItemPrice = (product) => {
    const quantity = toFiniteNumber(product?.quantity) || 1

    // Prefer the discounted/current price over basePrice (the original, pre-discount
    // price) so items on sale report the price the customer actually sees.
    const explicitUnitPrice =
        toFiniteNumber(product?.priceAfterItemDiscount) ??
        toFiniteNumber(product?.adjustedPrice) ??
        toFiniteNumber(product?.basePrice)

    if (explicitUnitPrice !== null) {
        return explicitUnitPrice
    }

    const price = toFiniteNumber(product?.price)
    if (price === null) {
        return product?.price
    }

    const lineTotal =
        toFiniteNumber(product?.priceTotal) ??
        toFiniteNumber(product?.adjustedPriceTotal) ??
        toFiniteNumber(product?.c_lineTotal)

    if (quantity > 1 && lineTotal !== null && Math.abs(lineTotal - price) < 0.0001) {
        return lineTotal / quantity
    }

    // Calculate discounted unit price if priceAdjustments exist
    if (product?.priceAdjustments && product.priceAdjustments.length > 0) {
        const totalAdjustment = product.priceAdjustments.reduce(
            (sum, adj) => sum + (toFiniteNumber(adj.price) || 0),
            0
        )
        const adjustedUnitPrice = price + totalAdjustment / quantity
        return adjustedUnitPrice
    }

    return price
}

/**
 * Constructs a Google Tag Manager (GTM) compatible item object from a product entity for analytics tracking.
 * Memoized to prevent redundant transformations for the same product - reduces TBT.
 *
 * @param {Object} product - The product object containing details such as id, name, price, brand, quantity, variants, and categories.
 * @param {number} index - The position of the item in the product list (used for GTM item indexing).
 * @param {Object} [recommendation] - Optional. The recommendation context, used to set item_list_id and item_list_name.
 * @returns {Object} An object representing the product in GTM's expected format.
 */
const getItem = (product, index, recommendation) => {
    // Check cache - WeakMap uses product object reference as key
    if (itemCache.has(product)) {
        const cached = itemCache.get(product)
        // Return cached if index and recommendation match (these affect the result)
        if (cached.index === index && cached.recommendation === recommendation) {
            return cached.result
        }
    }

    let itemId = 'productId' in product ? product.productId : product.id

    if ('c_variationGroup' in product) {
        itemId = product.c_variationGroup;
    } else{
        var productVariationGroups = product.variationGroups
        var productvariationValues = product.variationValues

        if (productVariationGroups && productvariationValues) {
            const matchingGroup = productVariationGroups.find(group => {
                const color = group?.variationValues?.color
                return color && color === productvariationValues?.color
            })

            if (matchingGroup) {
                itemId = matchingGroup.productId
            }
        }
    }

    const pid = 'productId' in product ? product.productId : product.id
    const name = 'productName' in product ? product.productName : product.name
    const resolvedIndex = Number.isFinite(Number(index)) ? Number(index) : null
    const fallbackIndex = Number.isFinite(Number(product.c_gtm_index))
        ? Number(product.c_gtm_index)
        : 0

    let prodObj = {
        item_name: name?.toLowerCase(),
        item_id: itemId,
        price: getUnitItemPrice(product),
        quantity: product.quantity || 1,
        index: resolvedIndex !== null ? resolvedIndex : fallbackIndex,
        item_group_id: itemId.substring(0, itemId.length - 3),
        item_list_id: recommendation ? recommendation.id : 'search_results_page',
        item_list_name: recommendation ? recommendation.name : 'search results'
    }

    const rawBrand =
        product.c_brand ||
        product.brand ||
        product.c_gtmBrand ||
        product.representedProduct?.c_gtmBrand

    if (rawBrand) {
        prodObj.item_brand = rawBrand.toLowerCase()
    }

    if (product.c_size) {
        prodObj.item_size = product.c_size.toLowerCase()
    }

    if (product.c_itemAddLocation) {
        prodObj.item_add_location = product.c_itemAddLocation
    }

    let categories

    if (product.representedProduct) {
        prodObj.sku = product.representedProduct.id

        if (product.representedProduct.c_refinementColor) {
            prodObj.item_variant = product.representedProduct.c_refinementColor.toLowerCase()
        }
        if (product.representedProduct.c_categories) {
            categories = JSON.parse(JSON.stringify(product.representedProduct.c_categories))
        }
        if (!prodObj.item_brand && product.representedProduct.c_gtmBrand) {
            prodObj.item_brand = product.representedProduct.c_gtmBrand.toLowerCase()
        }

        prodObj.item_color = (
            product.representedProduct?.c_colorName || product?.c_colorName
        )?.toLowerCase()
    } else if (product.variants && product.variants.length) {
        prodObj.sku = product.variants[0].productId

        if (product.c_refinementColor) {
            prodObj.item_variant = product.c_refinementColor.toLowerCase()
        }

        if (product.c_categories) {
            categories = JSON.parse(JSON.stringify(product.c_categories))
        }

        prodObj.item_color = product?.c_colorName?.toLowerCase()
    } else {
        prodObj.sku = pid

        if (product.c_refinementColor) {
            prodObj.item_variant = product.c_refinementColor.toLowerCase()
        }

        if (product.c_categories) {
            categories = JSON.parse(JSON.stringify(product.c_categories))
        }

        prodObj.item_color = product?.c_colorName?.toLowerCase()
    }

    if (categories && categories.length) {
        categories.reverse()

        if (categories[0].length <= 1) {
            categories.shift()
        }

        if (categories.length > 0) {
            const category = categories[0].toLowerCase()
            prodObj.item_category = category

            if (categories.length > 1) {
                const category2 = categories[1].toLowerCase()
                prodObj.item_category2 = category2

                if (categories.length > 2) {
                    const category3 = categories[2].toLowerCase()
                    prodObj.item_category3 = category3
                }
            }
        }
    }

    if (product?.priceAdjustments && product.priceAdjustments.length > 0) {

        for (var i = 0; i < product.priceAdjustments.length; i++) {
            const adjustment = product.priceAdjustments[i]
            prodObj.coupon = adjustment?.couponCode
            prodObj.coupon_value = Math.abs(adjustment?.price)
        }
    }

    // Cache the result
    itemCache.set(product, {index, recommendation, result: prodObj})

    return prodObj
}

/**
 * Generates a summary object containing the total item count and an array of item details from a given list.
 * @param {Array<Object>} list - An array of item objects, each expected to have a 'quantity' property.
 * @param {boolean} getIndex - If true, uses a custom function to determine each item's index based on its product ID.
 * @param {Object} [recommendation] - Optional recommendation object to associate with each item; if not provided, it is determined per product.
 * @returns {{count: number, items: Array<Object>}} An object with the total quantity of all items and an array of item details generated by getItem.
 */
const getItems = (list, getIndex, recommendation) => {
    let itemCount = 0
    let items = []

    for (var i = 0; i < list?.length; i++) {
        let index = i
        let product = list[i]
        const pid = 'productId' in product ? product.productId : product.id

        if (getIndex) {
            index = getProductIndex(pid)

            if (index < 0) {
                index = i
            }
        }

        if (!recommendation) {
            recommendation = getRecommendation(pid)
        }

        items.push(getItem(product, index, recommendation))
        itemCount += product.quantity
    }

    return {
        count: itemCount,
        items: items
    }
}

const parseFiniteNumber = (value) => {
    if (value === null || value === undefined) {
        return null
    }

    if (typeof value === 'string' && value.trim() === '') {
        return null
    }

    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
}

const getShipmentValue = (shipment) => {
    const shipmentTotal = parseFiniteNumber(shipment?.shippingTotal)
    if (shipmentTotal !== null) {
        return shipmentTotal
    }

    const adjustedShipmentTotal = parseFiniteNumber(shipment?.adjustedShippingTotal)
    if (adjustedShipmentTotal !== null) {
        return adjustedShipmentTotal
    }

    const shippingMethodPrice = parseFiniteNumber(shipment?.shippingMethod?.price)
    if (shippingMethodPrice !== null) {
        return shippingMethodPrice
    }

    return 0
}

/**
 * Generates a plain JavaScript object representing the current basket/cart for GTM (Google Tag Manager) dataLayer.
 *
 * Includes details such as currency, order totals, unique item count, coupon information, shipping tier, payment types, and item details.
 * Optionally includes shipping and payment information based on provided flags.
 *
 * @param {Object} basket - The basket object containing cart data, including currency, totals, product items, shipments, payment instruments, and order price adjustments.
 * @param {boolean} includeShippingInfo - Whether to include shipping tier information from the basket's shipments.
 * @param {boolean} includePaymentInfo - Whether to include payment type and gift card usage information from the basket's payment instruments.
 * @returns {Object} cartObj - A plain object with cart summary and details, suitable for GTM dataLayer
 */
const getBasket = (basket, includeShippingInfo, includePaymentInfo) => {
    var cartObj = {
        currency: basket?.currency,
        value: getBasketValue(basket),
        cart_unique_item_count: basket?.productItems?.length,
        cart_value_net: basket?.productTotal
    }
    var isBopis = false

    let couponCode
    let couponValue = null

    if (basket.orderPriceAdjustments?.length) {
        let totalDiscount = 0
        for (var i = 0; i < basket.orderPriceAdjustments?.length; i++) {
            if (basket.orderPriceAdjustments[i].couponCode) {
                // Use the first coupon code found
                if (!couponCode) {
                    couponCode = basket.orderPriceAdjustments[i].couponCode
                }
                // Sum all coupon discounts
                const discountValue = toFiniteNumber(basket.orderPriceAdjustments[i].price)
                if (discountValue !== null) {
                    totalDiscount += Math.abs(discountValue)
                }
            }
        }
        if (totalDiscount > 0) {
            couponValue = totalDiscount
        }
    }

    if (!couponCode && basket.productItems?.length) {
        let totalDiscount = 0
        for (var j = 0; j < basket.productItems.length; j++) {
            const productItem = basket.productItems[j]
            if (productItem.priceAdjustments?.length) {
                for (var m = 0; m < productItem.priceAdjustments.length; m++) {
                    const adjustment = productItem.priceAdjustments[m]
                    if (adjustment.couponCode) {
                        // Use the first coupon code found
                        if (!couponCode) {
                            couponCode = adjustment.couponCode
                        }
                        // Sum all coupon discounts
                        const discountValue = toFiniteNumber(adjustment.price)
                        if (discountValue !== null) {
                            totalDiscount += Math.abs(discountValue)
                        }
                    }
                }
            }
        }
        if (totalDiscount > 0) {
            couponValue = totalDiscount
        }
    }

    if (!couponCode && basket.couponItems?.length) {
        let totalDiscount = 0
        for (var k = 0; k < basket.couponItems?.length; k++) {
            const item = basket.couponItems[k]
            const code = item?.couponCode || item?.code
            if (code) {
                // Use the first coupon code found
                if (!couponCode) {
                    couponCode = code
                }
                // Sum all coupon discounts
                const couponItemValue =
                    toFiniteNumber(item?.price) ??
                    toFiniteNumber(item?.adjustedPrice) ??
                    toFiniteNumber(item?.appliedDiscount)
                if (couponItemValue !== null) {
                    totalDiscount += Math.abs(couponItemValue)
                }
            }
        }
        if (totalDiscount > 0) {
            couponValue = totalDiscount
        }
    }

    if (couponCode) {
        cartObj.coupon = couponCode
    }

    if (couponValue !== null) {
        cartObj.coupon_value = couponValue
    }

    if (includeShippingInfo && basket.shipments?.length) {
        if (basket.shipments[0].shippingMethod) {
            const shipment = basket.shipments[0]
            const shippingMethod = shipment.shippingMethod
            cartObj.shipping_tier = shippingMethod.name
            cartObj.shipping = getShipmentValue(shipment)
            isBopis = shippingMethod.c_storePickupEnabled
        }
    }

    if (includePaymentInfo && basket.paymentInstruments?.length) {
        let paymentType, secondaryType, secondaryAmount
        cartObj.gift_card_used = false

        for (var j = 0; j < basket.paymentInstruments.length; j++) {
            var pi = basket.paymentInstruments[j]
            if (pi.paymentMethodId == 'SMARTCLIXX') {
                if (!paymentType) {
                    paymentType = 'gift card'
                } else {
                    secondaryType = 'gift card'
                }

                cartObj.gift_card_used = true
                cartObj.gift_card_amount = pi.amount
            } else {
                if (pi.c_signifydPaymentData) {
                    try {
                        const data = JSON.parse(pi.c_signifydPaymentData)
                        const method = normalizePaymentType(data?.paymentMethod?.type)

                        if (!paymentType) {
                            paymentType = method
                        } else {
                            secondaryType = method
                            secondaryAmount = pi.amount
                        }
                    } catch (error) {
                        console.error('Error parsing c_signifydPaymentData:', error)
                    }
                } else if (pi.c_Adyen_Payment_Method_Variant) {
                    const pMethod = normalizePaymentType(pi.c_Adyen_Payment_Method_Variant)

                    if (!paymentType) {
                        paymentType = pMethod
                    } else {
                        secondaryType = pMethod
                        secondaryAmount = pi.amount
                    }
                } else {
                    const methodId = normalizePaymentType(pi.paymentMethodId)
                    if (!paymentType) {
                        paymentType = methodId
                    } else {
                        secondaryType = methodId
                        secondaryAmount = pi.amount
                    }
                }
            }
        }

        const normalizedPrimary = normalizePaymentType(paymentType)
        const normalizedSecondary = normalizePaymentType(secondaryType)
        const secondaryAmountValue = toFiniteNumber(secondaryAmount)

        // If primary is adyencomponent, try to use secondary as primary
        let finalPaymentType = normalizedPrimary
        if (normalizedPrimary === 'adyencomponent' && normalizedSecondary && normalizedSecondary !== 'adyencomponent') {
            finalPaymentType = normalizedSecondary
            cartObj.payment_type = finalPaymentType
        } else if (normalizedPrimary !== 'adyencomponent') {
            cartObj.payment_type = finalPaymentType
        }

        const hasDistinctSecondary =
            normalizedSecondary &&
            normalizedSecondary !== 'adyencomponent' &&
            normalizedSecondary !== finalPaymentType &&
            (secondaryAmountValue === null || secondaryAmountValue > 0)

        if (hasDistinctSecondary) {
            cartObj.secondary_payment_type = normalizedSecondary

            if (secondaryAmountValue !== null) {
                cartObj.secondary_payment_amount = secondaryAmountValue
            }
        }
    }

    const list = getItems(basket.productItems, true, null)

    cartObj.cart_item_count = list.count
    cartObj.items = list.items

    if (includeShippingInfo || includePaymentInfo) {
        cartObj.bopis_analytics = {
            availability: isBopis,
            coverage: isBopis,
            selected: isBopis
        }
    }

    return cartObj
}

/**
 * Constructs a base object for review-related GTM (Google Tag Manager) events on the product detail screen.
 * @param {Object} product - The product object to be included in the review event.
 * @returns {Object} An object containing screen metadata and an array of items formatted for GTM.
 */
const getReviewBase = (product) => {
    return {
        screen_name: 'product detail screen',
        screen_category: 'e-commerce',
        screen_section: 'reviews_section',
        items: [getItem(product)]
    }
}

/**
 * Asynchronously computes the SHA-256 hash of a given string and returns it as a hexadecimal string.
 * @param {string} value - The input string to be hashed.
 * @returns {Promise<string>} A promise that resolves to the SHA-256 hash of the input as a hex string, or an empty string if input is falsy.
 */
const toSHA256 = async (value) => {
    if (!value) return ''

    const encoder = new TextEncoder()
    const data = encoder.encode(value)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashHex = hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('')
    return hashHex
}

/**
 * Saves or removes a product index entry in sessionStorage under the 'productIndices' key.
 * If isRemoval is true, removes the entry for the given productId; otherwise, adds or updates the product index.
 *
 * @param {string|number} productId - The unique identifier for the product.
 * @param {number} index - The index value to associate with the product.
 * @param {boolean} isRemoval - If true, removes the product index entry; if false, adds or updates it.
 */
const saveProductIndex = (productId, index, isRemoval) => {
    let productIndices = JSON.parse(window.sessionStorage.getItem('productIndices')) || []

    if (isRemoval) {
        productIndices = productIndices.filter((item) => item.productId !== productId)
    } else {
        // Check if this productId already exists
        const existingIndex = productIndices.findIndex((item) => item.productId === productId)
        if (existingIndex >= 0) {
            // Update existing entry instead of creating duplicate
            productIndices[existingIndex].index = index
        } else {
            // Only push if new
            productIndices.push({productId, index})
        }
    }

    if (productIndices.length) {
        window.sessionStorage.setItem('productIndices', JSON.stringify(productIndices))
    } else {
        window.sessionStorage.removeItem('productIndices')
    }
}

/**
 * Retrieves the index of a product from sessionStorage based on the provided productId.
 * @param {string|number} productId - The unique identifier of the product to search for.
 * @returns {number} The index of the product if found; otherwise, returns 0.
 */
const getProductIndex = (productId) => {
    let productIndices = JSON.parse(window.sessionStorage.getItem('productIndices')) || []
    let index = -1

    for (var i = 0; i < productIndices.length; i++) {
        if (productIndices[i].productId === productId) {
            index = productIndices[i].index
            break
        }
    }

    return index
}

/**
 * Checks if a recommendation with the given name has already been sent and updates session storage accordingly.
 * Formats the name by converting it to lowercase and removing spaces before checking.
 *
 * @param {string} name - The name of the recommendation to check.
 * @returns {boolean} - Returns true if the recommendation has already been sent or if no name is provided; otherwise, returns false after marking it as sent.
 */
const isRecommendationSent = (name) => {
    if (!name) return true

    let recommendations = JSON.parse(window.sessionStorage.getItem('gtmRecommendations')) || []

    const nameFormatted = name.toLowerCase().replace(/\s/g, '')

    if (recommendations.indexOf(nameFormatted) < 0) {
        recommendations.push(nameFormatted)
        window.sessionStorage.setItem('gtmRecommendations', JSON.stringify(recommendations))

        return false
    }

    return true
}

/**
 * Stores a temporary recommendation name in sessionStorage under the key 'gtmTempRecommendation'.
 * The name is converted to lowercase and trimmed of whitespace before storage.
 *
 * @param {Object} obj - The recommendation object to store temporarily. If falsy, nothing is stored.
 */
const setTempRecommendation = (obj) => {
    if (obj) {
        var temp = {
            id: obj.id.toLowerCase().trim(),
            name: obj.name.toLowerCase().trim()
        }
        window.sessionStorage.setItem('gtmTempRecommendation', JSON.stringify(temp))
    }
}

/**
 * Saves or removes a product recommendation in sessionStorage under the 'gtmProductRecommendation' key.
 * If isRemoval is true, removes the product recommendation for the specified productId; otherwise, adds or updates it.
 * Cleans up the sessionStorage key if no recommendations remain.
 *
 * @param {string|number} productId - The unique identifier of the product.
 * @param {any} recommendation - The recommendation data to associate with the product.
 * @param {boolean} isRemoval - Whether to remove (true) or add/update (false) the recommendation.
 * @returns {void}
 */
const saveRecommendation = (productId, recommendation, isRemoval) => {
    let productRecommendation = JSON.parse(window.sessionStorage.getItem('gtmProductRecommendation')) || []

    if (isRemoval) {
        productRecommendation = productRecommendation.filter((item) => item.productId !== productId)
    } else {
        let found = false

        for (var i = 0; i < productRecommendation.length; i++) {
            if (productRecommendation[i].productId === productId) {
                productRecommendation[i].recommendation = recommendation
                found = true
                break
            }
        }

        if (!found) {
            productRecommendation.push({productId, recommendation})
        }
    }

    if (productRecommendation.length) {
        window.sessionStorage.setItem('gtmProductRecommendation', JSON.stringify(productRecommendation))
    } else {
        window.sessionStorage.removeItem('gtmProductRecommendation')
    }
}

/**
 * Retrieves the recommendation associated with a given product ID from sessionStorage.
 * If a temporary recommendation ('gtmTempRecommendation') exists, it is returned, removed from storage,
 * and persisted using saveRecommendation. Otherwise, searches the 'gtmProductRecommendation' array in sessionStorage
 * for a matching productId and returns its recommendation value.
 *
 * @param {string} productId - The unique identifier of the product for which to retrieve the recommendation.
 * @returns {string|undefined} The recommendation string if found; otherwise, undefined.
 */
const getRecommendation = (productId) => {
    let recommendation

    if (window.sessionStorage.getItem('gtmTempRecommendation')) {
        recommendation = JSON.parse(window.sessionStorage.getItem('gtmTempRecommendation'))
        window.sessionStorage.removeItem('gtmTempRecommendation')
        saveRecommendation(productId, recommendation, false)
        return recommendation
    }

    let productRecommendation = JSON.parse(window.sessionStorage.getItem('gtmProductRecommendation')) || []

    for (var i = 0; i < productRecommendation.length; i++) {
        if (productRecommendation[i].productId === productId) {
            recommendation = productRecommendation[i].recommendation
            break
        }
    }

    return recommendation
}

// STANDARD EVENTS

/**
 * Sends a user session event with anonymized and contextual user data to GTM.
 *
 * @param {Object} customer - The customer object containing user details.
 * @param {Object} location - The location object, typically from the router, providing page context.
 * @param {Object} [locale] - Locale from useMultiSite(); used for country_code and language_code.
 * @param {string} [brandId] - Active brand id (garage | dynamite).
 * @returns {Promise<void>} Resolves when the event has been sent.
 */
async function sendUserSession(customer, location, locale, brandId) {
    if (!customer || !brandId || !locale?.id) return
    if (isGtmHandoffPath(location?.pathname || getCurrentPathname())) return

    const isGuest = !customer.isRegistered
    const {country_code, language_code} = getGtmLocaleCodes(locale)
    const parameters = {
        user_anonymous: isGuest,
        user_authenticated: !isGuest,
        user_registered: !isGuest,
        visitor_login_state: isGuest ? 'guests' : 'registered',
        brand: brandId,
        country_code,
        language_code,
        is_mobile: 'No'
    }

    Object.assign(parameters, getPageInfo(location))

    if (!isGuest) {
        Object.assign(parameters, await getUser(customer))
    }

    sendEvent('user_session', parameters)

    window.sessionStorage.removeItem('gtmRecommendations')
}

/**
 * Sends a login event to the tracking system with user details and login method.
 * @param {Object} customer - The customer object representing the logged-in user.
 * @param {string} method - The login method used.
 * @returns {Promise<void>} Resolves when the event has been sent; returns early if customer is not provided.
 */
async function sendLoginEvent(customer, method) {
    if (!customer) return
    method = method.toLowerCase().trim()
    let parameters = await getUser(customer)
    parameters.method = method

    sendEvent('login', parameters)
}

/**
 * Sends a Google Tag Manager 'sign_up' event after successful email registration
 * that requires verification (fires on the verification-redirect path only).
 * @param {string} [interactionLocation] - Where sign-up started (native|checkout|guest_to_member|loyalty_enroll).
 * @param {string} [method='email_password'] - Sign-up method (google|apple|email_password|facebook).
 * @returns {Promise<void>} Resolves when the event has been sent.
 */
async function sendSignUpEventVerification(
    interactionLocation = SIGN_UP_INTERACTION_LOCATION.NATIVE,
    method = 'email_password'
) {
    const normalizedMethod = normalizeSignUpMethod(method)
    const location = isValidSignUpInteractionLocation(interactionLocation)
        ? interactionLocation
        : SIGN_UP_INTERACTION_LOCATION.NATIVE

    if (!normalizedMethod) return

    sendEvent('sign_up', {
        method: normalizedMethod,
        interaction_location: location
    })
}

/**
 * Sends a Google Tag Manager 'sign_up' event with user parameters.
 * @param {Object} customer - The customer object containing user information.
 * @param {string} method - The sign up method used (google|apple|email_password|facebook).
 * @param {string} [interactionLocation] - Where sign-up started; falls back to persisted/native.
 * @returns {Promise<void>} Resolves when the event has been sent.
 */
async function sendSignUpEvent(customer, method, interactionLocation) {
    if (!customer) return

    const normalizedMethod = normalizeSignUpMethod(method)
    if (!normalizedMethod) return

    const location = isValidSignUpInteractionLocation(interactionLocation)
        ? interactionLocation
        : consumeSignUpInteractionLocation()

    let parameters = await getUser(customer)
    parameters.method = normalizedMethod
    parameters.interaction_location = location

    sendEvent('sign_up', parameters)
}

/**
 * Sends a Google Tag Manager 'view_item' event for a given product.
 * Constructs the event parameters including currency, price, and item details,
 * then dispatches the event using the sendEvent utility.
 *
 * @param {Object} product - The product object containing details such as currency and price.
 * @param {string} interactionLocation - The location of the interaction (e.g. 'pdp', 'plp', etc.).
 * @returns {void}
 */
function sendViewItemEvent(product, interactionLocation) {
    const pid = 'productId' in product ? product.productId : product.id
    
    // If the payload already has an index (e.g., from PDP passing initialGTMIndex), use it
    if (typeof product.index === 'number' && product.index >= 0) {
        let recommendation = getRecommendation(pid)
        
        // Still save under all relevant product IDs for consistency
        saveProductIndex(pid, product.index, false)
        if (product.representedProduct?.id) {
            saveProductIndex(product.representedProduct.id, product.index, false)
        }
        try {
            if (typeof window !== 'undefined') {
                const ctx = JSON.parse(window.sessionStorage.getItem('plp-productContext'))
                if (ctx?.productId && ctx.productId !== pid && ctx.productId !== product.representedProduct?.id) {
                    saveProductIndex(ctx.productId, product.index, false)
                }
            }
        } catch {}

        const parameters = {
            currency: product.currency || product.c_currency,
            value: product.price,
            items: [getItem(product, product.index, recommendation)],
            ...(interactionLocation && {interaction_location: interactionLocation})
        }

        sendEvent('view_item', parameters)
        return
    }
    
    // Otherwise, resolve the index as before
    let index = getProductIndex(pid)
    let recommendation = getRecommendation(pid)

    if (index < 0) {
        // Fallback 1: Check plp-productContext for the master product that was clicked on PLP
        try {
            if (typeof window !== 'undefined') {
                const ctx = JSON.parse(window.sessionStorage.getItem('plp-productContext'))
                if (ctx?.productId) {
                    const lookupIndex = getProductIndex(ctx.productId)
                    if (lookupIndex >= 0) {
                        index = lookupIndex
                    }
                }
            }
        } catch (e) {
        }

        // Fallback 2: Use plp-productContext.index directly if available
        if (index < 0) {
            try {
                if (typeof window !== 'undefined') {
                    const ctx = JSON.parse(window.sessionStorage.getItem('plp-productContext'))
                    const ctxIndex = Number(ctx?.index)
                    if (Number.isFinite(ctxIndex) && ctxIndex >= 0) {
                        index = ctxIndex
                    } else {
                        index = 0
                    }
                } else {
                    index = 0
                }
            } catch {
                index = 0
            }
        }
    }

    // Save the resolved index under all relevant product IDs so downstream events (add_to_cart, view_cart)
    // can find it regardless of which product ID they look up (variation group, variant SKU, or master).
    saveProductIndex(pid, index, false)
    if (product.representedProduct?.id) {
        saveProductIndex(product.representedProduct.id, index, false)
    }
    try {
        if (typeof window !== 'undefined') {
            const ctx = JSON.parse(window.sessionStorage.getItem('plp-productContext'))
            if (ctx?.productId && ctx.productId !== pid && ctx.productId !== product.representedProduct?.id) {
                saveProductIndex(ctx.productId, index, false)
            }
        }
    } catch {}

    const parameters = {
        currency: product.currency || product.c_currency,
        value: product.price,
        items: [getItem(product, index, recommendation)],
        ...(interactionLocation && {interaction_location: interactionLocation})
    }

    sendEvent('view_item', parameters)
}

/**
 * Sends a Google Tag Manager 'view_item_list' event with the specified category and item list details.
 * @param {Object} category - The category object containing the list's id and name.
 * @param {Array} itemList - The array of items to be included in the event.
 * @param {boolean} isCarousel - Indicates if the item list is displayed in a carousel (recommendation) context.
 * @param {string} interactionLocation - The location of the interaction (e.g. 'menu_navigation', 'pdp', 'plp', etc.).
 * @returns {void}
 */
function sendViewItemListEvent(category, itemList, isCarousel, interactionLocation) {
    if (isCarousel && isRecommendationSent(category?.name)) {
        return
    }

    let parameters = {
        id: category?.id,
        name: category?.name?.toLowerCase(),
        interaction_location: interactionLocation
    }

    const list = getItems(itemList, false, parameters)

    parameters.items = list.items

    sendEvent('view_item_list', parameters)
}

/**
 * Sends a Google Tag Manager event for viewing search results with the given search term and item list.
 * @param {string} searchTerm - The search term entered by the user.
 * @param {Array<Object>} itemList - The list of items resulting from the search.
 * @returns {void}
 */
function sendViewSearchResultsEvent(searchTerm, itemList) {
    let parameters = {
        search_term: searchTerm
    }

    const list = getItems(itemList, false, null)

    parameters.items = list.items

    sendEvent('view_search_results', parameters)
}

/**
 * Builds the product payload for select_item analytics from a product tile,
 * using the swatch-selected variation group instead of the initially rendered hit.
 * @param {Object} product - Tile pricing/search product data for the active color.
 * @param {string} selectedProductId - Active variation group id on the tile.
 * @param {Object} [options] - Optional selection context.
 * @param {string} [options.colorValue] - Selected color attribute value (swatch).
 * @returns {Object|null} Product shaped for GTM item helpers.
 */
function buildProductTileSelectItemProduct(product, selectedProductId, options = {}) {
    if (!product) {
        return null
    }

    const {colorValue} = options
    const resolvedProductId = selectedProductId || product.productId || product.id

    const colorAttrValues =
        product.variationAttributes?.find((attr) => attr.id === 'color')?.values ||
        product.representedProduct?.variationAttributes?.find((attr) => attr.id === 'color')
            ?.values ||
        []

    const selectedColorAttr = colorValue
        ? colorAttrValues.find((value) => value.value === colorValue)
        : null

    const colorName =
        selectedColorAttr?.name ||
        product.c_colorName ||
        product.representedProduct?.c_colorName ||
        null

    const isSelectedColorProduct =
        product.id === resolvedProductId || product.productId === resolvedProductId

    const refinementColor = isSelectedColorProduct
        ? product.c_refinementColor || product.representedProduct?.c_refinementColor || null
        : null

    const representedProduct = {
        ...(product.representedProduct || {})
    }

    if (colorName) {
        representedProduct.c_colorName = colorName
    }

    if (refinementColor) {
        representedProduct.c_refinementColor = refinementColor
    } else if (!isSelectedColorProduct) {
        delete representedProduct.c_refinementColor
    }

    return {
        ...product,
        productId: resolvedProductId,
        id: resolvedProductId,
        c_variationGroup: resolvedProductId,
        ...(colorName && {c_colorName: colorName}),
        ...(refinementColor
            ? {c_refinementColor: refinementColor}
            : !isSelectedColorProduct
              ? {c_refinementColor: undefined}
              : {}),
        representedProduct
    }
}

/**
 * Sends a Google Tag Manager 'select_item' event with product and category details.
 * @param {Object} product - The product object containing details such as currency and price.
 * @param {Object} category - The category object containing the category id and name.
 * @param {number} index - The position of the item in the product list
 * @param {string} interactionLocation - The location of the interaction (e.g. 'pdp', 'wishlist_page', etc.).
 * @returns {void}
 */
function sendSelectItemEvent(product, category, index, interactionLocation) {
    if (!product) {
        return
    }

    const pid = 'productId' in product ? product.productId : product.id
    const parameters = {
        currency: product.currency,
        value: product.price,
        item_list_id: category?.id,
        item_list_name: category?.name,
        items: [getItem(product, index)],
        interaction_location: interactionLocation
    }

    // CRITICAL: Clear the productIndices array when user clicks a new product on PLP
    // This ensures we don't use stale cached entries from previous products
    window.sessionStorage.removeItem('productIndices')

    saveProductIndex(pid, index, false)

    if (interactionLocation) {
        setSessionStorageItem(GTM_SELECT_ITEM_LOCATION_KEY, interactionLocation)
    }

    sendEvent('select_item', parameters)
}

/**
 * Sends a Google Tag Manager event for adding or removing a product from the wishlist.
 * @param {Object} product - The product object containing details such as currency and price.
 * @param {boolean} isRemoval - Indicates whether the event is for removal (true) or addition (false) to the wishlist.
 * @param {number} index - The position of the item in the product list
 * @param {string} interactionLocation - The location of the interaction (e.g. 'pdp', 'wishlist_page', etc.).
 * @returns {void}
 */
function sendWishlistEvent(product, isRemoval, index, interactionLocation) {
    const event = !isRemoval ? 'add_to_wishlist' : 'remove_from_wishlist'
    let parameters = {
        currency: product.currency,
        value: product.price,
        items: [getItem(product, index)],
        interaction_location: interactionLocation
    }

    if (!isRemoval) {
        parameters.interaction_context = 'product_page'
        parameters.interaction_detail = 'wishlist_button'
    }

    sendEvent(event, parameters)
}

/**
 * Sends a Google Tag Manager event for adding or removing an item from the cart.
 * @param {Object} product - The product object containing details such as currency and price.
 * @param {boolean} isRemoval - Indicates whether the event is for item removal (true) or addition (false).
 * @param {number} pdpIndex - The index of the product in the PDP product list.
 * @param {string} interactionLocation - The location of the interaction (e.g. 'pdp', 'plp', etc.).
 * @returns {void}
 */
function sendItemCartEvent(product, isRemoval, pdpIndex, interactionLocation) {
    const event = !isRemoval ? 'add_to_cart' : 'remove_from_cart'
    const pid = 'productId' in product ? product.productId : product.id
    let index = 0
    let recommendation

    if (isRemoval) {
        saveProductIndex(pid, index, true)
        saveRecommendation(pid, null, true)
    } else {
        recommendation = getRecommendation(pid)

        // Resolve the list index from the best available source (in priority order):
        // 1. c_gtm_index on the product object (explicit override) — takes precedence over cache
        // 2. productIndices lookup for the variation group (same-color PLP click)
        // 3. Caller-supplied pdpIndex (passed from PDP handleAddToCart)
        // 4. plp-productContext sessionStorage — authoritative list position written
        //    by the PLP on product click; covers all add-to-cart flows (PDP, Quick
        //    View, product tile) without requiring each caller to pass the index.
        // 5. First entry in productIndices (for Quick View flows where plp-productContext
        //    is cleared between PLP click and add-to-cart).
        // 6. Cached product index (only if all above fail)
        const resolveFallbackIndex = () => {
            if (Number.isFinite(Number(pdpIndex)) && Number(pdpIndex) >= 0) {
                return Number(pdpIndex)
            }
            try {
                if (typeof window === 'undefined') return -1
                const ctx = JSON.parse(window.sessionStorage.getItem('plp-productContext'))
                const i = Number(ctx?.index)
                if (Number.isFinite(i) && i >= 0) return i
                
                // Fallback: if plp-productContext is null/cleared, use the first productIndices entry
                // (from the last PLP click). This handles Quick View flows where the sessionStorage
                // context is cleared between PLP click and add-to-cart.
                const productIndices = JSON.parse(window.sessionStorage.getItem('productIndices')) || []
                if (Array.isArray(productIndices) && productIndices.length > 0) {
                    const firstIndex = productIndices[0]?.index
                    return Number.isFinite(firstIndex) ? Number(firstIndex) : -1
                }
                
                return -1
            } catch {
                return -1
            }
        }

        if (Number.isFinite(product.c_gtm_index)) {
            // Explicit override: always use c_gtm_index if provided
            index = product.c_gtm_index
            saveProductIndex(pid, index, false)
        } else if (product.c_variationGroup) {
            index = getProductIndex(product.c_variationGroup)

            if (index < 0) {
                index = resolveFallbackIndex()
                if (index >= 0) {
                    saveProductIndex(pid, index, false)
                } else {
                    index = getProductIndex(pid)
                    if (index < 0) index = 0
                }
            } else {
                saveProductIndex(product.c_variationGroup, 0, true)
                saveProductIndex(pid, index, false)
            }
        } else {
            index = resolveFallbackIndex()
            if (index >= 0) {
                saveProductIndex(pid, index, false)
            } else {
                index = getProductIndex(pid)
                if (index < 0) index = 0
            }
        }

        if (!recommendation && product.c_variationGroup) {
            recommendation = getRecommendation(product.c_variationGroup)

            if (recommendation) {
                saveRecommendation(product.c_variationGroup, null, true)
                saveRecommendation(pid, recommendation, false)
            }
        }
    }

    let parameters = {
        currency: product.c_currency || product.currency,
        value: getEventItemValue(product),
        items: [getItem(product, index, recommendation)],
        ...(interactionLocation && {interaction_location: interactionLocation})
    }

    sendEvent(event, parameters)
}

/**
 * Sends a Google Tag Manager 'view_cart' event with the current basket's parameters.
 * @param {Object} basket - The current shopping basket object to extract parameters from.
 * @returns {void}
 */
function sendViewCartEvent(basket) {
    const parameters = getBasket(basket)
    sendEvent('view_cart', parameters)
}

/**
 * Sends a Google Tag Manager 'begin_checkout' event with the current basket parameters.
 * @param {Object} basket - The current shopping basket object to extract parameters from.
 * @returns {void}
 */
function sendBeginCheckoutEvent(basket) {
    const parameters = getBasket(basket)
    sendEvent('begin_checkout', parameters)
}

/**
 * Sends a Google Tag Manager event when a shipping address is added to the basket.
 * Adds the shipping tier information to the event parameters if available.
 *
 * @param {Object} basket - The current basket object containing shipment and shipping method details.
 * @returns {void}
 */
function sendShippingAddressEvent(basket) {
    const parameters = getBasket(basket, true)
    sendEvent('add_shipping_address', parameters)
}

/**
 * Sends a Google Tag Manager event for shipping information when a user adds shipping details to their basket.
 * Extracts shipping tier information from the first shipment in the basket, if available, and includes it in the event parameters.
 *
 * @param {Object} basket - The basket object containing shipment and shipping method details.
 * @returns {void}
 */
function sendShippingInfoEvent(basket) {
    const parameters = getBasket(basket, true)
    sendEvent('add_shipping_info', parameters)
}

/**
 * Sends a Google Tag Manager event for adding payment information.
 * Retrieves relevant basket parameters and triggers the 'add_payment_info' event.
 *
 * @param {Object} basket - The current basket object containing payment and order details.
 * @returns {void}
 */
function sendPaymentInfoEvent(basket) {
    const parameters = getBasket(basket, true, true)
    sendEvent('add_payment_info', parameters)
}

/**
 * Sends a Google Tag Manager 'purchase' event with order details.
 * The order number is remembered in localStorage so the event is only ever sent once,
 * even if the shopper reopens the confirmation page in a new tab or after closing it.
 * @param {Object} order - The order object containing purchase information.
 * @returns {void}
 */
function sendPurchaseEvent(order) {
    if (typeof window === 'undefined' || !order?.orderNo) return

    if (getTrackedPurchaseOrders().includes(order.orderNo)) return

    trackPurchaseOrder(order.orderNo)

    let parameters = getBasket(order, true, true)
    parameters.affiliation = 'web'
    parameters.tax = order.taxTotal
    parameters.transaction_id = order.orderNo
    sendEvent('purchase', parameters)
    window.sessionStorage.removeItem('productIndices')
    window.sessionStorage.removeItem('gtmProductRecommendation')
}

// CUSTOM EVENTS

/**
 * Sends a 'notify_me' event to the GTM data layer with product details.
 * @param {Object} product - The product object containing details for the event.
 * @returns {void}
 */
function sendNotifyMeEvent(product) {
    const parameters = {
        currency: product.currency,
        value: product.price,
        items: [getItem(product)]
    }

    sendEvent('notify_me', parameters)
}

/**
 * Sends a 'load_review' GTM event with product review data when reviews are available.
 * @param {Object} product - The product object for which reviews are being loaded.
 * @param {Object} reviews - The reviews object containing review data, including Results and TotalResults.
 * @returns {void}
 */
function sendLoadReviewEvent(product, reviews) {
    if (!product || !Array.isArray(reviews?.Results) || reviews.Results.length === 0) return
    let parameters = getReviewBase(product)
    parameters.review_count = reviews.TotalResults
    parameters.review_viewed = reviews.Results.length

    sendEvent('load_review', parameters)
}

/**
 * Sends a Google Tag Manager event when product reviews are sorted.
 * @param {Object} product - The product object for which the review sort event is triggered.
 * @param {string} sort - The type of sort applied to the reviews (e.g., 'newest', 'highest_rating').
 * @returns {void}
 */
function sendSortReviewEvent(product, sort) {
    if (!product) return
    let parameters = getReviewBase(product)
    parameters.sort_type = sort.toLowerCase()

    sendEvent('sort_review', parameters)
}

/**
 * Sends a Google Tag Manager event when a user initiates writing a product review.
 * Stores the current timestamp in sessionStorage under 'writeReviewTime' and triggers the 'write_review' event with product-specific parameters.
 *
 * @param {Object} product - The product object for which the review is being written.
 * @returns {void}
 */
function sendWriteReviewEvent(product) {
    if (!product) return
    const parameters = getReviewBase(product)

    window.sessionStorage.setItem('writeReviewTime', Date.now())

    sendEvent('write_review', parameters)
}

/**
 * Sends a Google Tag Manager event when a product review is submitted, including review details and submission duration.
 * @param {Object} product - The product object for which the review is being submitted.
 * @param {Object} review - The review object containing user input.
 * @param {Object} submissionId - The unique identifier for the submitted review.
 * @returns {void}
 */
function sendSubmitReviewEvent(product, review, submissionId) {
    if (!product || !review) return
    let parameters = getReviewBase(product)

    parameters.rating = review.rating
    parameters.review_id = submissionId
    parameters.review_text = review.reviewtext
    parameters.review_length = review.reviewtext.length
    parameters.review_word_count = review.reviewtext.split(' ').length

    if (window.sessionStorage.getItem('writeReviewTime')) {
        const duration = Date.now() - window.sessionStorage.getItem('writeReviewTime')
        parameters.submission_duration_ms = duration
        window.sessionStorage.removeItem('writeReviewTime')
    }

    sendEvent('submit_review', parameters)
}

/**
 * Sends a Google Tag Manager event indicating a failed login attempt.
 * @param {string} method - The login method used.
 * @returns {void}
 */
function sendLoginFailedEvent(method) {
    const parameters = {
        method: method
    }

    sendEvent('login_failed', parameters)
}

/**
 * Sends a 'view_error' event to the GTM data layer with the specified error type.
 * @param {string} error - The type or message of the error to be reported.
 * @returns {void}
 */
function sendViewErrorEvent(error) {
    const parameters = {
        error_type: error
    }
    sendEvent('view_error', parameters)
}

/**
 * Sends a "contact_us" event to the tracking system with customer and topic information.
 * @param {Object} customer - The customer object for whom the event is being sent.
 * @param {string} topic - The topic or subject of the contact request.
 * @returns {Promise<void>} Resolves when the event has been sent; does nothing if customer is falsy.
 */
async function sendContactUsEvent(customer, topic) {
    if (!customer) return
    let parameters = await getUser(customer)
    parameters.topic = topic

    sendEvent('contact_us', parameters)
}

/**
 * Builds the less detailed item array used by notify_me / shop_similar custom_click events.
 * @param {Object} product
 * @returns {Object}
 */
const getCustomClickItem = (product) => {
    const item = getItem(product)
    return {
        item_id: item.item_id,
        item_group_id: item.item_group_id,
        sku: item.sku,
        item_name: item.item_name,
        item_brand: item.item_brand,
        item_variant: item.item_variant
    }
}

/**
 * Sends a click event to the event tracking system with the specified type.
 * Optionally includes product item details when a product is provided.
 * @param {string} type - The type of click event to be sent.
 * @param {Object} [product] - Optional product object to include as `items`.
 * @returns {void}
 */
function sendClickEvent(type, product) {
    const parameters = {
        type: type
    }
    if (product) {
        const lessDetailedItem =
            type === 'notify_me - pdp' ||
            type === 'notify_me - plp' ||
            type === 'shop_similar - pdp'
        parameters.items = [lessDetailedItem ? getCustomClickItem(product) : getItem(product)]
    }
    sendEvent('custom_click', parameters)
}

/**
 * Sends a custom event to the Google Tag Manager dataLayer with specified parameters.
 * @param {string} eventName - The name of the event to be pushed to the dataLayer.
 * @param {Object} parameters - Additional key-value pairs to include with the event.
 * @returns {void}
 */
const sendEvent = (eventName, parametersObj) => {
    if (typeof window === 'undefined') return

    const currentPageKey = getUserSessionPageKey()
    if (eventName === 'user_session' && lastUserSessionPageKey === currentPageKey) {
        return
    }

    const shouldQueueUntilUserSession =
        process.env.NODE_ENV !== 'test' &&
        eventName !== 'user_session' &&
        lastUserSessionPageKey !== currentPageKey

    if (shouldQueueUntilUserSession) {
        queuedGtmEvents.push({eventName, parametersObj})
        return
    }

    const config = getConfig();
    const appConfig = config.app;
    var parameters = parametersObj;
    var gtmAddGA4Wrapper = getSiteEnvVarValue(appConfig, 'GTM_ADD_GA4WRAPPER', null);

    if (gtmAddGA4Wrapper === 'true' || gtmAddGA4Wrapper === true) {
        if (eventName != 'user_session') {
            if (
                eventName != 'login' &&
                eventName != 'sign_up' &&
                eventName != 'login_failed' &&
                eventName != 'custom_click'
            ) {
                parameters = {
                    ga4eec: {
                        ecommerce: parametersObj
                    }
                }
            }
        } else {
            parameters = {
                ecommerce: parametersObj,
                page: {
                    country_code: parametersObj.country_code,
                    brand: parametersObj.brand
                }
            }
        }
    }

    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({ ga4eec: { ecommerce: null } })
    window.dataLayer.push({
        event: eventName,
        ...parameters
    })

    if (eventName === 'user_session') {
        lastUserSessionPageKey = currentPageKey
        const queued = queuedGtmEvents
        queuedGtmEvents = []
        queued.forEach(({eventName: queuedName, parametersObj: queuedParameters}) => {
            sendEvent(queuedName, queuedParameters)
        })
    }
}

export {
    INTERACTION_LOCATION,
    SIGN_UP_INTERACTION_LOCATION,
    trackGtmPathname,
    resolveViewItemListInteractionLocation,
    resolveViewItemInteractionLocation,
    resolveProductTileViewItemLocation,
    resolveAddToCartInteractionLocation,
    resolveSignUpInteractionLocation,
    persistSignUpInteractionLocation,
    consumeSignUpInteractionLocation,
    normalizeSignUpMethod,
    initViewItemListInteractionTracking,
    sendUserSession,
    sendLoginEvent,
    sendSignUpEvent,
    sendViewItemEvent,
    sendViewItemListEvent,
    sendViewSearchResultsEvent,
    buildProductTileSelectItemProduct,
    sendSelectItemEvent,
    sendWishlistEvent,
    sendItemCartEvent,
    sendViewCartEvent,
    sendBeginCheckoutEvent,
    sendShippingAddressEvent,
    sendShippingInfoEvent,
    sendPaymentInfoEvent,
    sendPurchaseEvent,
    sendNotifyMeEvent,
    sendLoadReviewEvent,
    sendSortReviewEvent,
    sendWriteReviewEvent,
    sendSubmitReviewEvent,
    sendLoginFailedEvent,
    sendViewErrorEvent,
    sendContactUsEvent,
    sendClickEvent,
    sendEvent,
    setTempRecommendation,
    sendSignUpEventVerification,
    toSHA256
}
